type SelectOption = {
  value?: number | string;
  label?: string;
  __isNew__?: boolean;
  [key: string]: unknown;
};

type ReceivingInvoiceFormValues = {
  invoice_number?: string;
  supplier_selected?: SelectOption | null;
  carrier_selected?: SelectOption | null;
  truck_selected?: SelectOption | null;
  statusSelected?: SelectOption | null;
  status?: number;
  truck_question_1?: SelectOption | number | null;
  truck_question_2?: SelectOption | number | null;
  truck_question_3?: SelectOption | number | null;
  truck_question_4?: SelectOption | number | null;
  document_1?: unknown;
  document_2?: unknown;
};

type TruckQuestionValue = SelectOption | number | null | undefined;

function resolveTruckQuestion(value: TruckQuestionValue): number | undefined {
  if (value != null && typeof value === "object" && "value" in value) {
    return value.value as number | undefined;
  }
  return typeof value === "number" ? value : undefined;
}

/**
 * Build the create/update body for receiving invoice header endpoints.
 * Omits form-only state (items, selects UI leftovers, metadata) so large
 * item lists are never serialized into multipart saves.
 */
export function buildReceivingInvoicePayload(
  values: ReceivingInvoiceFormValues,
) {
  const body: Record<string, unknown> = {
    invoice_number: values.invoice_number,
    supplier_selected: values.supplier_selected,
    carrier_selected: values.carrier_selected,
    truck_selected: values.truck_selected,
    status: values.statusSelected?.value ?? values.status ?? 0,
    truck_question_1: resolveTruckQuestion(values.truck_question_1),
    truck_question_2: resolveTruckQuestion(values.truck_question_2),
    truck_question_3: resolveTruckQuestion(values.truck_question_3),
    truck_question_4: resolveTruckQuestion(values.truck_question_4),
  };

  if (values.document_1) {
    body.document_1 = values.document_1;
  }

  if (values.document_2) {
    body.document_2 = values.document_2;
  }

  return body;
}

/** Frontend-owned toast copy — never surface backend/PHP messages. */
export const RECEIVING_MESSAGES = {
  created: "Receiving invoice created",
  updated: "Receiving invoice updated",
  deleted: "Receiving invoice deleted",
  documentDeleted: "Document deleted",
  documentDeleteFailed: "Could not delete document.",
  restarted: "Receiving restarted fresh",
  createFailed: "Could not create receiving invoice.",
  updateFailed: "Could not update receiving invoice.",
  deleteFailed: "Could not delete receiving invoice.",
  loadFailed: "Could not load receiving invoice.",
  listLoadFailed: "Could not load receiving invoices.",
  closedDelete: "Cannot delete closed receiving.",
  itemAdded: "Item added",
  itemUpdated: "Receiving item updated",
  itemDeleted: "Receiving item deleted",
  itemsDeleted: "Receiving items deleted",
  itemAddFailed: "Could not add receiving item.",
  itemUpdateFailed: "Could not update receiving item.",
  itemDeleteFailed: "Could not delete receiving item.",
  itemsDeleteFailed: "Could not delete receiving items.",
  unitCostUpdated: "Unit cost updated",
  totalCostUpdated: "Total cost updated",
  costUpdateFailed: "Could not update cost.",
  reportFailed: "Could not generate report.",
  loadTrucksFailed: "Could not load trucks.",
  loadSuppliersFailed: "Could not load suppliers.",
  loadEmployeesFailed: "Could not load employees.",
  loadProductFailed: "Could not load product.",
  loadWarehousesFailed: "Could not load warehouses.",
  loadLocationsFailed: "Could not load warehouse locations.",
  loadCarriersFailed: "Could not load carriers.",
} as const;
